/**
 * Product View Transaction Dialogue
 * Detail dialog for product inventory transactions.
 * Shows: type, date, quantity, product+variant, location, lot/batch, PO/cost info, notes.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, Chip, Button, Card, CardContent,
  IconButton, Alert, Divider, useTheme, alpha
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TRANSACTION_TYPE_MAP, formatTransactionDate } from './transactionConstants';

function formatDateOnly(dateStr) {
  if (!dateStr) return 'N/A';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString();
}

const cardSx = (theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: 'white',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
  }
});

function InfoRow({ label, value }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || 'Not specified'}
      </Typography>
    </Box>
  );
}

export default function ProductViewTransactionDialogue({
  open,
  onClose,
  transaction,
  timeFormat
}) {
  const theme = useTheme();

  if (!transaction) return null;

  const tx          = transaction;
  const typeInfo    = TRANSACTION_TYPE_MAP[tx.transaction_type];
  const qty         = parseFloat(tx.quantity ?? tx.quantity_change ?? 0);
  const isNeg       = qty < 0;
  const isTransfer  = tx.transaction_type === 'transfer';
  const isUsage     = tx.transaction_type === 'usage';
  const isPurchase  = tx.transaction_type === 'purchase';

  const fmtCurrency = (v) => v != null && v !== '' ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
  const unitCost    = parseFloat(tx.unit_cost)     || 0;
  const taxAmt      = parseFloat(tx.tax_amount)    || 0;
  const shipCost    = parseFloat(tx.shipping_cost) || 0;
  const totalCost   = Math.abs(qty) * unitCost + taxAmt + shipCost;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          maxHeight: '90vh',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          bgcolor: alpha(theme.palette.grey[50], 0.95),
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        pb: 2, pt: 3, px: 3,
        fontSize: '1.25rem', fontWeight: 500, color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5, borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            {typeInfo?.icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Transaction Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              {typeInfo?.label || tx.transaction_type} — {formatDateOnly(
                (tx.transaction_date || tx.created_at || '').split('T')[0]
              )}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha(theme.palette.grey[400], 0.1) } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>

            {/* Transaction Summary */}
            <Grid item xs={12}>
              <Card sx={cardSx(theme)}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Transaction Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Type
                      </Typography>
                      {typeInfo ? (
                        <Chip
                          icon={typeInfo.icon}
                          label={typeInfo.label}
                          color={typeInfo.color}
                          size="medium"
                          sx={{
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: theme.palette[typeInfo.color]?.main || theme.palette.primary.main
                          }}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {tx.transaction_type || 'Unknown'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Date & Time
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatTransactionDate(tx.transaction_date || tx.created_at, timeFormat)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Quantity Change
                      </Typography>
                      {(() => {
                        const absQty       = Math.abs(qty);
                        const sign         = isNeg ? '-' : '+';
                        const color        = isNeg ? 'warning.main' : 'success.main';
                        const unitsPerCase = tx.units_per_case || 1;
                        if (unitsPerCase > 1) {
                          const cases          = Math.floor(absQty / unitsPerCase);
                          const remainingUnits = absQty % unitsPerCase;
                          return (
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color }}>
                                {sign}{cases.toLocaleString()} {cases === 1 ? 'case' : 'cases'}
                                {remainingUnits > 0 ? ` + ${remainingUnits.toLocaleString()} units` : ''}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                {absQty.toLocaleString()} units total
                              </Typography>
                            </Box>
                          );
                        }
                        return (
                          <Typography variant="h6" sx={{ fontWeight: 500, color }}>
                            {sign}{absQty.toLocaleString()} units
                          </Typography>
                        );
                      })()}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Product Details + Location Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{ ...cardSx(theme), height: '100%', minHeight: 200 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Product Details
                  </Typography>
                  <InfoRow label="Product" value={tx.products?.name} />
                  {tx.products?.sku && <InfoRow label="SKU" value={tx.products.sku} />}
                  {tx.variants?.name && <InfoRow label="Variant" value={tx.variants.name} />}
                  {tx.lot_number   && <InfoRow label="Lot Number"   value={tx.lot_number} />}
                  {tx.batch_number && <InfoRow label="Batch Number" value={tx.batch_number} />}
                  {tx.manufactured_date && <InfoRow label="Mfg Date"    value={formatDateOnly(tx.manufactured_date)} />}
                  {tx.expiry_date       && <InfoRow label="Expiry Date"  value={formatDateOnly(tx.expiry_date)} />}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ ...cardSx(theme), height: '100%', minHeight: 200 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Location Details
                  </Typography>
                  {isTransfer ? (
                    <>
                      <InfoRow label="From Location" value={tx.from_location?.name} />
                      <InfoRow label="To Location"   value={tx.to_location?.name} />
                    </>
                  ) : (
                    <InfoRow
                      label="Location"
                      value={tx.from_location?.name || tx.to_location?.name}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Purchase Order Details */}
            {isPurchase && (tx.po_number || tx.invoice_number || tx.delivery_status || tx.unit_cost) && (
              <Grid item xs={12}>
                <Card sx={cardSx(theme)}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                      Purchase Order Details
                    </Typography>
                    <Grid container spacing={3}>
                      {tx.po_number && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>PO Number</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{tx.po_number}</Typography>
                        </Grid>
                      )}
                      {tx.po_date && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>PO Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateOnly(tx.po_date)}</Typography>
                        </Grid>
                      )}
                      {tx.invoice_number && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Invoice Number</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{tx.invoice_number}</Typography>
                        </Grid>
                      )}
                      {tx.invoice_date && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Invoice Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateOnly(tx.invoice_date)}</Typography>
                        </Grid>
                      )}
                      {tx.invoice_due_date && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Invoice Due Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateOnly(tx.invoice_due_date)}</Typography>
                        </Grid>
                      )}
                      {tx.delivery_status && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Delivery Status</Typography>
                          <Chip
                            label={tx.delivery_status.charAt(0).toUpperCase() + tx.delivery_status.slice(1)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              bgcolor: tx.delivery_status === 'delivered'
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.warning.main, 0.12),
                              color: tx.delivery_status === 'delivered' ? 'success.main' : 'warning.main',
                              border: `1px solid ${tx.delivery_status === 'delivered'
                                ? alpha(theme.palette.success.main, 0.3)
                                : alpha(theme.palette.warning.main, 0.3)}`,
                            }}
                          />
                        </Grid>
                      )}
                      {tx.expected_delivery_date && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Expected Delivery</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateOnly(tx.expected_delivery_date)}</Typography>
                        </Grid>
                      )}
                      {tx.actual_delivery_date && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Actual Delivery</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateOnly(tx.actual_delivery_date)}</Typography>
                        </Grid>
                      )}
                    </Grid>

                    {(tx.unit_cost || tx.tax_amount || tx.shipping_cost) && (
                      <>
                        <Divider sx={{ my: 2.5 }} />
                        <Grid container spacing={3}>
                          {fmtCurrency(tx.unit_cost) && (
                            <Grid item xs={6} md={3}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Unit Cost</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>{fmtCurrency(tx.unit_cost)}</Typography>
                            </Grid>
                          )}
                          {fmtCurrency(tx.tax_amount) && (
                            <Grid item xs={6} md={3}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Tax Amount</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>{fmtCurrency(tx.tax_amount)}</Typography>
                            </Grid>
                          )}
                          {fmtCurrency(tx.shipping_cost) && (
                            <Grid item xs={6} md={3}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Shipping Cost</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>{fmtCurrency(tx.shipping_cost)}</Typography>
                            </Grid>
                          )}
                          {totalCost > 0 && (
                            <Grid item xs={6} md={3}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>Total Cost</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Usage alert */}
            {isUsage && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {(() => {
                      const absQty       = Math.abs(qty);
                      const unitsPerCase = tx.units_per_case || 1;
                      if (unitsPerCase > 1) {
                        const cases          = Math.floor(absQty / unitsPerCase);
                        const remainingUnits = absQty % unitsPerCase;
                        return (
                          <>
                            <strong>Usage Record:</strong> {cases.toLocaleString()} {cases === 1 ? 'case' : 'cases'}
                            {remainingUnits > 0 ? ` + ${remainingUnits.toLocaleString()} units` : ''} ({absQty.toLocaleString()} units total) consumed from inventory.
                          </>
                        );
                      }
                      return <><strong>Usage Record:</strong> {absQty.toLocaleString()} units consumed from inventory.</>;
                    })()}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Notes / PI Reference */}
            {tx.notes && (() => {
              const isPiDeduction = tx.notes.startsWith('PI Delivery Deduction');
              const piNumMatch = tx.notes.match(/PI #([^\s|—]+)/);
              const custMatch  = tx.notes.match(/Customer:\s*([^—|]+)/);
              const compMatch  = tx.notes.match(/Company:\s*([^—|]+)/);
              const piNumber   = piNumMatch?.[1]?.trim();
              const customer   = custMatch?.[1]?.trim();
              const company    = compMatch?.[1]?.trim();
              // User-supplied notes come after the last ' — '
              const userNotes  = isPiDeduction
                ? tx.notes.split(' — ').slice(2).join(' — ').trim() || null
                : tx.notes;

              return (
                <>
                  {isPiDeduction && (
                    <Grid item xs={12}>
                      <Card sx={{ ...cardSx(theme), border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Source Reference
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip label="PI Delivery Deduction" size="small" color="primary" sx={{ fontWeight: 500 }} />
                            {piNumber && <Chip label={`PI #${piNumber}`} size="small" variant="outlined" color="primary" sx={{ fontWeight: 500 }} />}
                            {customer && <Chip label={`Customer: ${customer}`} size="small" variant="outlined" sx={{ fontWeight: 500 }} />}
                            {company && <Chip label={`Company: ${company}`} size="small" variant="outlined" sx={{ fontWeight: 500 }} />}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {userNotes && (
                    <Grid item xs={12}>
                      <Card sx={cardSx(theme)}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Notes
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                            {userNotes}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              );
            })()}

          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, bgcolor: 'white' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, fontWeight: 500, color: 'text.secondary' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
